import type { AxisRange, Extent, HitRegion, PlotlyAxisInternals, PlotlyGraphDiv } from '@/types/plotlyChart';
import type { PlotlyHTMLElement } from 'plotly.js';

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const AXIS_HIT_PX = 44;

type TimeTrace = { x?: Array<string | number>; text?: string[] };

// Plotly date axes parse naive `YYYY-MM-DDTHH:mm:ss` as UTC clock (they
// discard any timezone). `new Date(string)` in the browser is local, so
// every x-axis number we hand Plotly has to go through this or the boxes
// land in a different epoch than the range and vanish.
export function toPlotlyMs(value: string | number): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }
    if (typeof value !== 'string') {
        return null;
    }
    const match = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d+))?)?/.exec(value);
    if (!match) {
        const time = new Date(value).getTime();
        return Number.isNaN(time) ? null : time;
    }
    const millisecond = match[7] ? Number((match[7] + '000').slice(0, 3)) : 0;
    return Date.UTC(
        Number(match[1]),
        Number(match[2]) - 1,
        Number(match[3]),
        Number(match[4]),
        Number(match[5]),
        Number(match[6] ?? 0),
        millisecond
    );
}

export function plotlyDayStart(time: number): number {
    const date = new Date(time);
    return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

function pad2(value: number): string {
    return String(value).padStart(2, '0');
}

function formatClockLabel(time: number): string {
    const date = new Date(time);
    return `${pad2(date.getUTCDate())} ${MONTH_LABELS[date.getUTCMonth()]} ${pad2(date.getUTCHours())}:${pad2(date.getUTCMinutes())}`;
}

function traceTimeLabels(trace: TimeTrace): Array<string | number> {
    const labels = trace.text?.filter((value) => toPlotlyMs(value) !== null);
    if (labels && labels.length > 0) {
        return labels;
    }
    return trace.x ?? [];
}

export function dataDays(plotlyData: TimeTrace[]): number[] {
    const days = new Set<number>();
    for (const trace of plotlyData) {
        for (const value of traceTimeLabels(trace)) {
            const time = toPlotlyMs(value);
            if (time === null) {
                continue;
            }
            days.add(plotlyDayStart(time));
        }
    }
    return Array.from(days).sort((a, b) => a - b);
}

export function formatCategoryTick(value: string): string {
    const time = toPlotlyMs(value);
    if (time === null) {
        return value;
    }
    return formatClockLabel(time);
}

export function categoryTimes(plotlyData: TimeTrace[]): string[] {
    const times = new Set<string>();
    for (const trace of plotlyData) {
        for (const value of traceTimeLabels(trace)) {
            if (typeof value === 'string' && toPlotlyMs(value) !== null) {
                times.add(value);
            }
        }
    }
    return Array.from(times).sort((a, b) => (toPlotlyMs(a) ?? 0) - (toPlotlyMs(b) ?? 0));
}

export function categoryExtent(count: number): Extent | null {
    if (count === 0) {
        return null;
    }
    return [-0.5, count - 0.5];
}

export function widenCategoryRange(range: Extent, limit: Extent | null): Extent {
    let [start, end] = range;
    if (end < start) {
        const swap = start;
        start = end;
        end = swap;
    }
    if (end - start < 1) {
        const mid = (start + end) / 2;
        start = mid - 0.5;
        end = mid + 0.5;
    }
    if (!limit) {
        return [start, end];
    }
    const [lo, hi] = limit;
    if (end - start >= hi - lo) {
        return [lo, hi];
    }
    if (start < lo) {
        end += lo - start;
        start = lo;
    }
    if (end > hi) {
        start -= end - hi;
        end = hi;
    }
    return [start, end];
}

export function visibleCategoryTicks(range: Extent, count: number, maxTicks: number): number[] {
    if (count === 0) {
        return [];
    }
    const start = Math.max(0, Math.ceil(range[0] - 1e-6));
    const end = Math.min(count - 1, Math.floor(range[1] + 1e-6));
    const visible: number[] = [];
    if (end < start) {
        const nearest = Math.min(count - 1, Math.max(0, Math.round((range[0] + range[1]) / 2)));
        return [nearest];
    }
    for (let index = start; index <= end; index += 1) {
        visible.push(index);
    }
    const step = Math.max(1, Math.ceil(visible.length / maxTicks));
    return visible.filter((_, index) => index % step === 0);
}

export function parsePlotlyNumber(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }
    if (typeof value === 'string') {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
}

export function nearlyEqualExtent(left: Extent, right: Extent, fraction = 0.03): boolean {
    const span = Math.max(Math.abs(right[1] - right[0]), 1);
    const epsilon = span * fraction;
    return Math.abs(left[0] - right[0]) <= epsilon && Math.abs(left[1] - right[1]) <= epsilon;
}

export function parseRelayoutExtent(event: Record<string, unknown>, axis: 'x' | 'y', parseValue: (value: unknown) => number | null): Extent | null {
    const bracket0 = event[`${axis}axis.range[0]`];
    const bracket1 = event[`${axis}axis.range[1]`];
    let start = parseValue(bracket0);
    let end = parseValue(bracket1);
    const range = event[`${axis}axis.range`];
    if (Array.isArray(range) && range.length >= 2) {
        start = parseValue(range[0]);
        end = parseValue(range[1]);
    }
    if (start === null || end === null) {
        return null;
    }
    return [Math.min(start, end), Math.max(start, end)];
}

// Round 1-2-5 stops from a millisecond to 100 seconds. A log axis only ever
// gets labelled with these, so the numbers stay ones a reader can place.
const LOG_STOPS = [0, 1, 2, 3, 4, 5].flatMap((power) => [1, 2, 5].map((step) => step * 10 ** power));

// What the log axis spans when there's nothing to plot. An empty log axis
// otherwise labels every minor tick it can fit.
export const EMPTY_MS_EXTENT: Extent = [20, 500];
export const EMPTY_MS_TICKS = [20, 50, 100, 200, 500];

type FenceTrace = { lowerfence?: number[]; upperfence?: number[] };

// Whisker-to-whisker span across every broker. Non-positive values are
// dropped: they have no position on a log axis.
export function yValueExtent(traces: FenceTrace[]): Extent | null {
    let min = Infinity;
    let max = -Infinity;
    for (const trace of traces) {
        for (const values of [trace.lowerfence, trace.upperfence]) {
            for (const value of values ?? []) {
                if (!Number.isFinite(value) || value <= 0) {
                    continue;
                }
                min = Math.min(min, value);
                max = Math.max(max, value);
            }
        }
    }
    return min === Infinity ? null : [min, max];
}

// Tick stops and range for the log axis: every round stop inside the data's
// span, widened to the next stop either side, then thinned to `maxTicks`.
export function logAxisTicks([min, max]: Extent, maxTicks: number): { tickvals: number[]; range: Extent } {
    const lastIndex = LOG_STOPS.length - 1;
    const below = LOG_STOPS.map((stop, index) => (stop <= min ? index : 0));
    const lowIndex = Math.min(lastIndex - 1, Math.max(...below));
    const above = LOG_STOPS.findIndex((stop) => stop >= max);
    const highIndex = Math.min(lastIndex, Math.max(above === -1 ? lastIndex : above, lowIndex + 1));
    const stops = LOG_STOPS.slice(lowIndex, highIndex + 1);
    const range: Extent = [Math.log10(Math.min(stops[0], min)), Math.log10(Math.max(stops.at(-1) ?? stops[0], max))];
    if (stops.length <= maxTicks) {
        return { tickvals: stops, range };
    }
    const count = Math.min(stops.length, maxTicks);
    const tickvals = Array.from({ length: count }, (_, index) => stops[Math.round((index * (stops.length - 1)) / (count - 1))]);
    return { tickvals, range };
}

export function niceLinearTicks([min, max]: Extent, maxTicks: number): number[] {
    const lo = Math.min(min, max);
    const hi = Math.max(min, max);
    const span = Math.max(hi - lo, Number.EPSILON);
    const raw = span / Math.max(maxTicks - 1, 1);
    const power = 10 ** Math.floor(Math.log10(raw));
    const residual = raw / power;
    let step = power;
    if (residual > 5) {
        step = 10 * power;
    } else if (residual > 2) {
        step = 5 * power;
    } else if (residual > 1) {
        step = 2 * power;
    }
    const first = Math.ceil(lo / step) * step;
    const ticks: number[] = [];
    for (let value = first; value <= hi + step * 1e-6; value += step) {
        ticks.push(Number(value.toPrecision(6)));
    }
    return ticks.length > 0 ? ticks : [lo, hi];
}

// 1-2-5 log stops inside the zoomed window. Deep zoom often lands between
// those stops, so fall back to even linear labels in that case.
export function visibleLogTicks([min, max]: Extent, maxTicks: number): number[] {
    const lo = Math.max(Math.min(min, max), 0.1);
    const hi = Math.max(min, max, lo * 1.01);
    const inside = LOG_STOPS.filter((stop) => stop >= lo && stop <= hi);
    if (inside.length >= 3) {
        if (inside.length <= maxTicks) {
            return inside;
        }
        return Array.from({ length: maxTicks }, (_, index) => inside[Math.round((index * (inside.length - 1)) / (maxTicks - 1))]);
    }
    return niceLinearTicks([lo, hi], maxTicks);
}

export function logRangeToMs([start, end]: Extent): Extent {
    const looksLinear = start > 10 || end > 10;
    if (looksLinear) {
        return [Math.max(start, 0.1), Math.max(end, 0.1)];
    }
    return [Math.max(10 ** start, 0.1), Math.max(10 ** end, 0.1)];
}

// `2.5k` rather than `2500` — the axis is narrow and the gutter is shared with the title.
export function formatMs(value: number): string {
    if (value >= 1000) {
        return `${Number((value / 1000).toPrecision(3))}k`;
    }
    if (Math.abs(value - Math.round(value)) < 1e-6) {
        return String(Math.round(value));
    }
    return String(Number(value.toPrecision(3)));
}

export function asGraphDiv(node: PlotlyHTMLElement | null): PlotlyGraphDiv | null {
    if (!node) {
        return null;
    }
    const layout = (node as PlotlyGraphDiv)._fullLayout;
    if (!layout?.xaxis || !layout?.yaxis) {
        return null;
    }
    return node as PlotlyGraphDiv;
}

function axisLinearRange(axis: PlotlyAxisInternals): Extent {
    return [axis.r2l(axis.range[0]), axis.r2l(axis.range[1])];
}

export function scaleAxisRange(axis: PlotlyAxisInternals, factor: number, centerFraction: number): AxisRange {
    const [start, end] = axisLinearRange(axis);
    const center = start + (end - start) * centerFraction;
    return [axis.l2r(center + (start - center) * factor), axis.l2r(center + (end - center) * factor)];
}

export function panAxisRange(axis: PlotlyAxisInternals, pixels: number): AxisRange {
    const [start, end] = axisLinearRange(axis);
    const delta = (pixels / Math.max(axis._length, 1)) * (end - start);
    return [axis.l2r(start + delta), axis.l2r(end + delta)];
}

export function clampAxisRange(axis: PlotlyAxisInternals, next: AxisRange, data: AxisRange, minSpanFloor = 1e-9): AxisRange {
    // `next` is already in range coordinates. On a log axis those are log10,
    // while `data` is still raw values, so only the data bounds go through `d2l`.
    const lo = axis.d2l(Number(data[0]));
    const hi = axis.d2l(Number(data[1]));
    if (!Number.isFinite(lo) || !Number.isFinite(hi)) {
        return next;
    }
    const full: AxisRange = [axis.l2r(lo), axis.l2r(hi)];
    let start = axis.r2l(next[0]);
    let end = axis.r2l(next[1]);
    if (!Number.isFinite(start) || !Number.isFinite(end)) {
        return full;
    }
    if (end < start) {
        const swap = start;
        start = end;
        end = swap;
    }
    const dataSpan = hi - lo;
    const minSpan = Math.max(dataSpan / 250, minSpanFloor);
    let span = end - start;
    if (span < minSpan) {
        const mid = (start + end) / 2;
        start = mid - minSpan / 2;
        end = mid + minSpan / 2;
        span = minSpan;
    }
    if (span >= dataSpan) {
        return [axis.l2r(lo), axis.l2r(hi)];
    }
    if (start < lo) {
        end += lo - start;
        start = lo;
    }
    if (end > hi) {
        start -= end - hi;
        end = hi;
    }
    if (start < lo) {
        return [axis.l2r(lo), axis.l2r(hi)];
    }
    return [axis.l2r(start), axis.l2r(end)];
}

export function hitRegion(gd: PlotlyGraphDiv, clientX: number, clientY: number): HitRegion {
    const xa = gd._fullLayout.xaxis;
    const ya = gd._fullLayout.yaxis;
    const rect = gd.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    const plotLeft = xa._offset;
    const plotRight = xa._offset + xa._length;
    const plotTop = ya._offset;
    const plotBottom = ya._offset + ya._length;
    if (x >= plotLeft && x <= plotRight && y > plotBottom && y <= plotBottom + AXIS_HIT_PX) {
        return 'x';
    }
    if (y >= plotTop && y <= plotBottom && x < plotLeft && x >= plotLeft - AXIS_HIT_PX) {
        return 'y';
    }
    if (x >= plotLeft && x <= plotRight && y >= plotTop && y <= plotBottom) {
        return 'plot';
    }
    return null;
}

function formatHoverMs(value: number): string {
    return `${Math.round(value)} ms`;
}

type BoxHoverHit = {
    name: string;
    when: string;
    stats: number[];
    center: number;
};

export function boxUnderPointer(gd: PlotlyGraphDiv, clientX: number, clientY: number): BoxHoverHit | null {
    const xa = gd._fullLayout.xaxis;
    const ya = gd._fullLayout.yaxis;
    const rect = gd.getBoundingClientRect();
    const x = clientX - rect.left - xa._offset;
    const y = clientY - rect.top - ya._offset;
    let closest: BoxHoverHit | null = null;
    for (const series of gd.calcdata ?? []) {
        const head = series[0];
        const trace = head?.trace;
        const placement = head?.t;
        if (!head || trace?.type !== 'box' || !placement) {
            continue;
        }
        series.forEach((point, index) => {
            const x0 = xa.c2p(point.pos + placement.bPos - placement.bdPos, true);
            const x1 = xa.c2p(point.pos + placement.bPos + placement.bdPos, true);
            const y0 = ya.c2p(point.min, true);
            const y1 = ya.c2p(point.max, true);
            const left = Math.min(x0, x1);
            const right = Math.max(x0, x1);
            const top = Math.min(y0, y1);
            const bottom = Math.max(y0, y1);
            if (x < left || x > right || y < top || y > bottom) {
                return;
            }
            const raw = trace.customdata?.[index];
            if (!raw) {
                return;
            }
            const center = (left + right) / 2;
            const distance = Math.abs(x - center);
            if (closest && distance >= Math.abs(x - closest.center)) {
                return;
            }
            const when = raw[8];
            closest = {
                name: trace.name ?? '',
                when: typeof when === 'string' ? formatCategoryTick(when) : '',
                stats: raw.slice(0, 8).map(Number),
                center,
            };
        });
    }
    return closest;
}

export function renderBoxTip(tip: HTMLDivElement, hit: BoxHoverHit) {
    const [median, mean, p25, p75, p10, p90, p99, stdDev] = hit.stats;
    const rows: Array<[string, string]> = [
        ['Median', formatHoverMs(median)],
        ['Mean', formatHoverMs(mean)],
        ['P25–P75', `${Math.round(p25)}–${Math.round(p75)} ms`],
        ['P10–P90', `${Math.round(p10)}–${Math.round(p90)} ms`],
        ['P99', formatHoverMs(p99)],
        ['Std dev', formatHoverMs(stdDev)],
    ];
    tip.replaceChildren();
    if (hit.when) {
        const when = document.createElement('div');
        when.className = 'mb-1 text-[11px] text-slate-500';
        when.textContent = hit.when;
        tip.appendChild(when);
    }
    const title = document.createElement('div');
    title.className = 'mb-1 font-semibold text-slate-800';
    title.textContent = hit.name;
    tip.appendChild(title);
    for (const [label, value] of rows) {
        const row = document.createElement('div');
        row.className = 'flex items-baseline justify-between gap-4';
        const labelNode = document.createElement('span');
        labelNode.className = 'text-slate-400';
        labelNode.textContent = label;
        const valueNode = document.createElement('span');
        valueNode.textContent = value;
        row.append(labelNode, valueNode);
        tip.appendChild(row);
    }
}

export function placeBoxTip(container: HTMLElement, tip: HTMLDivElement, clientX: number, clientY: number) {
    const bounds = container.getBoundingClientRect();
    tip.style.display = 'block';
    const tipBounds = tip.getBoundingClientRect();
    let left = clientX - bounds.left + 14;
    let top = clientY - bounds.top + 14;
    if (left + tipBounds.width > bounds.width - 8) {
        left = clientX - bounds.left - tipBounds.width - 14;
    }
    if (top + tipBounds.height > bounds.height - 8) {
        top = clientY - bounds.top - tipBounds.height - 14;
    }
    tip.style.left = `${Math.max(8, left)}px`;
    tip.style.top = `${Math.max(8, top)}px`;
}

export function hideBoxTip(tip: HTMLDivElement | null) {
    if (tip) {
        tip.style.display = 'none';
    }
}

export function cursorForRegion(region: HitRegion): string {
    if (region === 'x') {
        return 'ew-resize';
    }
    if (region === 'y') {
        return 'ns-resize';
    }
    if (region === 'plot') {
        return 'grab';
    }
    return '';
}
